<template>
  <section class="ws-home">
    <!-- ===== HERO ===== -->
    <section class="ws-wrap hero">
      <div class="hero-grid ws-stagger" v-reveal>
        <div class="hero-copy ws-reveal" :style="{ '--i': 0 }">
          <h1 class="hero-title">
            Een <span class="accent">nieuwe start</span> voor het hele gezin.
          </h1>
          <p class="hero-lead">
            Na een heftige periode van ziekte verdient ieder gezin rust en
            ruimte om weer op adem te komen. Wij bieden gezinnen een gratis
            verblijf om samen op te laden en opnieuw te beginnen.
          </p>
          <div class="hero-cta">
            <DoneerKnoppen>
              <template #activator="{ props }">
                <button v-bind="props" class="btn btn-primary">
                  <span class="heart">♥</span> Doneer nu
                </button>
              </template>
            </DoneerKnoppen>
            <button
              class="btn btn-ghost"
              @click="$router.push('/overons#werkwijze')"
            >
              Hoe het werkt →
            </button>
          </div>
          <div class="hero-stats">
            <div>
              <div class="stat-num">100%</div>
              <div class="stat-label">gratis voor gezinnen</div>
            </div>
            <div class="stat-divider"></div>
            <div>
              <div class="stat-num">ANBI</div>
              <div class="stat-label">erkende stichting</div>
            </div>
          </div>
        </div>

        <div class="hero-media ws-reveal" :style="{ '--i': 1 }">
          <div class="hero-photo">
            <v-img
              :src="require('@/assets/stock/kind.avif')"
              alt="Lachend kind buiten in de natuur"
              :eager="true"
              cover
              height="100%"
            ></v-img>
            <div class="hero-photo-shade"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== THREE CARDS ===== -->
    <section id="over" class="ws-wrap cards ws-stagger" v-reveal>
      <div class="card ws-reveal" :style="{ '--i': 0 }">
        <span class="card-icon">
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
            focusable="false"
          >
            <circle cx="9" cy="7" r="2.8" />
            <path
              d="M4 19.5v-1.2a5 5 0 0 1 10 0v1.2a.8.8 0 0 1-.8.8H4.8a.8.8 0 0 1-.8-.8Z"
            />
            <circle cx="16" cy="9.5" r="2.2" />
            <path
              d="M12.5 19.5v-.8a3.8 3.8 0 0 1 7.6 0v.8a.7.7 0 0 1-.7.7h-6.2a.7.7 0 0 1-.7-.7Z"
            />
          </svg>
        </span>
        <h3>Over ons</h3>
        <p>
          Wij helpen gezinnen waar een kind ernstig ziek is (geweest) een
          moeilijke periode samen af te sluiten, weer op te laden en het begin
          van een nieuwe toekomst te vieren.
        </p>
        <router-link class="card-link" to="/overons">Lees verder →</router-link>
      </div>

      <div class="card ws-reveal" :style="{ '--i': 1 }">
        <span class="card-icon">
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            fill-rule="evenodd"
            clip-rule="evenodd"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M2 12a10 10 0 1 0 20 0 10 10 0 1 0-20 0ZM4.2 12a7.8 7.8 0 1 0 15.6 0 7.8 7.8 0 1 0-15.6 0Z"
            />
            <path d="M15.4 8.6 10.6 10.6 8.6 15.4 13.4 13.4Z" />
          </svg>
        </span>
        <h3>Hoe werkt het</h3>
        <p>
          Verhuurders stellen hun vakantiehuis via Micazu kosteloos beschikbaar.
          Een partnerziekenhuis draagt het gezin bij ons voor, waarna Micazu
          passende voorstellen doet. Zo kan het gezin even op adem komen.
        </p>
        <router-link class="card-link" to="/overons#werkwijze"
          >Lees verder →</router-link
        >
      </div>

      <div class="card card-doneer ws-reveal" :style="{ '--i': 2 }">
        <span class="blob"></span>
        <span class="card-icon card-icon-light">
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M12 20.3C11 19.5 4 14.5 4 9.4 4 7 5.9 5.2 8.2 5.2 9.7 5.2 11.1 6 12 7.3 12.9 6 14.3 5.2 15.8 5.2 18.1 5.2 20 7 20 9.4 20 14.5 13 19.5 12 20.3Z"
            />
          </svg>
        </span>
        <h3>Doneer nu</h3>
        <p>
          Donaties zijn de belangrijkste bron van onze inkomsten. Ziet u het
          belang van WeShare4Kids? Dan zijn de gezinnen waarvoor wij ons
          inzetten ontzettend blij met uw bijdrage.
        </p>
        <DoneerKnoppen>
          <template #activator="{ props }">
            <button v-bind="props" class="card-cta-white">♥ Doneer</button>
          </template>
        </DoneerKnoppen>
      </div>
    </section>

    <!-- ===== TESTIMONIAL ===== -->
    <section class="ws-quote" v-if="reviews && reviews.length">
      <v-window v-model="reviewIndex" show-arrows="hover" class="quote-window">
        <v-window-item
          v-for="(review, index) in reviews"
          :key="index"
          :value="index"
        >
          <div class="quote-inner">
            <div class="quote-mark">“</div>
            <p class="quote-text">
              {{ review.review }}
            </p>
            <p class="quote-author">{{ review.name }}</p>
          </div>
        </v-window-item>
      </v-window>
    </section>

    <!-- ===== KINDEREN EN ERNSTIGE ZIEKTES ===== -->
    <section class="ws-wrap split ws-reveal" v-reveal>
      <div class="split-media">
        <v-img
          :src="require('@/assets/stock/ziekte.jpg')"
          alt="Ziek kind tijdens behandeling"
          cover
          aspect-ratio="1"
        ></v-img>
      </div>
      <div class="split-copy">
        <span class="eyebrow">Onze missie</span>
        <h2>Kinderen en ernstige ziektes</h2>
        <p>
          Wij zijn van mening dat elk kind recht heeft op een zorgeloze jeugd,
          gevuld met liefde, vreugde en kansen. Helaas worden veel kinderen, en
          dus gezinnen, geconfronteerd met de harde realiteit van ernstige
          ziektes, die niet alleen fysiek, maar ook emotioneel en mentaal heftig
          ingrijpen.
        </p>
        <p>
          Samen bouwen we aan een wereld waarin elk kind de kans krijgt om te
          gedijen. Met uw steun kunnen we een positief verschil maken in het
          leven van kinderen en gezinnen die het hard nodig hebben. Laten we
          samen ons ‘tweede huis’ delen en bijdragen aan een betere toekomst.
        </p>
      </div>
    </section>

    <!-- ===== ZWARE PERIODE (soft band) ===== -->
    <section class="band-soft">
      <div class="ws-wrap split reverse ws-reveal" v-reveal>
        <div class="split-copy">
          <span class="eyebrow">De realiteit</span>
          <h2>Een zware periode voor het hele gezin</h2>
          <p>
            Voor gezinnen waarin een kind lijdt of heeft geleden aan een
            ernstige ziekte is het moeilijk om de overgang te maken van de
            behandelfase terug naar het ‘normale’ leven. In deze periode
            ontbreekt vaak de vertrouwde gezinsdynamiek en zijn de
            energiereserves uitgeput.
          </p>
          <p>
            Een medisch traject vergt heel veel van het kind, maar evenzeer van
            de ouders en de broertjes en zusjes. De vertrouwde rust verdwijnt en
            de gezinsdynamiek verandert. Vaak moeten bepaalde dingen wijken om
            herstel te bespoedigen, en dat begrijpen de andere kinderen niet
            altijd.
          </p>
          <p>
            Daarom bieden we met WeShare4Kids een gratis ‘gezinsverblijf’ aan,
            gericht op het ondersteunen van gezinnen ná de behandeling van
            ernstige ziektes bij kinderen.
          </p>
        </div>
        <div class="split-media tall">
          <v-img
            :src="require('@/assets/stock/ziekenhuis.jpg')"
            alt="Gang in een ziekenhuis"
            cover
            height="100%"
          ></v-img>
        </div>
      </div>
    </section>

    <!-- ===== EEN NIEUWE START ===== -->
    <section class="ws-wrap split ws-reveal" v-reveal>
      <div class="split-media tall">
        <v-img
          :src="require('@/assets/stock/strand.jpg')"
          alt="Gezin op het strand"
          cover
          height="100%"
        ></v-img>
      </div>
      <div class="split-copy">
        <span class="eyebrow">Ons doel</span>
        <h2>Een nieuwe start</h2>
        <p>
          WeShare4Kids wil een lichtpuntje zijn in de duisternis: een bron van
          steun en hoop voor gezinnen die getroffen zijn door ziekte. Wij
          geloven in de kracht van gemeenschapszin en solidariteit, en willen
          een platform bieden waarop mensen elkaar kunnen vinden en
          ondersteunen.
        </p>
        <p>
          Wij richten ons op het weer bij elkaar brengen van het gezin in een
          ontspannen sfeer, door een gratis verblijf in een andere omgeving aan
          te bieden, kosteloos beschikbaar gesteld door eigenaren van
          vakantiewoningen. Een ‘oplaadmoment’, misschien zelfs een nieuwe start
          voor het hele gezin.
        </p>
      </div>
    </section>

    <!-- ===== STAT BAND ===== -->
    <section class="ws-wrap stat-band-wrap">
      <div class="stat-band ws-reveal" v-reveal>
        <span class="stat-blob stat-blob-1"></span>
        <span class="stat-blob stat-blob-2"></span>
        <div class="stat-band-inner">
          <span class="eyebrow eyebrow-warm"
            >Prinses Máxima Centrum voor kinderoncologie</span
          >
          <h2 class="stat-headline">
            Jaarlijks krijgen ongeveer
            <span class="amber">600 kinderen</span> in Nederland kanker.
          </h2>
          <p>
            Nog steeds overlijdt één op de vier kinderen met kanker aan deze
            ziekte. Als een kind ernstig ziek is door kanker, staat maar één
            ding voorop: genezen.
          </p>
        </div>
      </div>
    </section>

    <!-- ===== NIEUWS ===== -->
    <section id="nieuws" class="ws-wrap news">
      <div class="news-head">
        <div>
          <span class="eyebrow">Op de hoogte blijven</span>
          <h2>Ons laatste nieuws</h2>
        </div>
        <router-link class="link-strong" to="/nieuws#alles"
          >Bekijk alle artikelen →</router-link
        >
      </div>
      <div class="news-grid ws-stagger" v-reveal>
        <router-link
          class="news-card ws-reveal"
          v-for="(n, index) in nieuws"
          :key="index"
          :style="{ '--i': index }"
          :to="`/nieuws/${n.uid}`"
        >
          <div class="news-img">
            <v-img
              :src="n.header_image"
              :alt="n.title"
              aspect-ratio="1.6"
              cover
            ></v-img>
          </div>
          <div class="news-body">
            <span class="news-date">{{
              n.location + " " + getDate(n.timestamp)
            }}</span>
            <h3>{{ n.title }}</h3>
            <p>{{ n.subtitle }}</p>
            <span class="card-link">Lees verder →</span>
          </div>
        </router-link>
      </div>
    </section>

    <PartnerSection />
  </section>
</template>

<script>
import PartnerSection from "@/components/PartnerSection.vue";
import { mapState } from "vuex";
import { DateTime } from "luxon";
import DoneerKnoppen from "@/components/DoneerKnoppen.vue";

export default {
  components: {
    PartnerSection,
    DoneerKnoppen,
  },
  data() {
    return {
      reveal: false,
      reviewIndex: 0,
    };
  },
  computed: {
    ...mapState({
      nieuws: (state) => state.nieuws,
      reviews: (state) => state.reviews,
    }),
  },
  mounted() {
    this.$store.dispatch("getReviews");
  },
  methods: {
    getDate(d) {
      let i = DateTime.fromSeconds(d);
      return i.toLocaleString(DateTime.DATE_FULL);
    },
  },
};
</script>

<style lang="scss" scoped>
$cream: #fbf4ec;
$orange: #ea6b26;
$orange-dark: #d65b1b;
$accent: #c2541a;
$ink: #2a2018;
$ink-strong: #241b12;
$muted: #5c4f44;

.ws-home {
  font-family: "Nunito Sans", sans-serif;
  color: $ink;
}

.ws-wrap {
  max-width: 1200px;
  margin: 0 auto;
  padding: 48px 32px;
  width: 100%;
}

.accent {
  color: $orange;
}

.eyebrow {
  font-family: "Nunito", sans-serif;
  font-weight: 800;
  font-size: 13.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $accent;
  display: block;
}
.eyebrow-warm {
  color: #f7a86a;
}

/* ===== HERO ===== */
.hero {
  padding: 64px 32px 40px;
}
.hero-grid {
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  gap: 56px;
  align-items: center;
}
.badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: "Nunito", sans-serif;
  font-weight: 700;
  font-size: 14px;
  color: $accent;
  background: #fbe3d2;
  padding: 8px 16px;
  border-radius: 100px;
}
.badge-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: $orange;
}
.hero-title {
  font-family: "Nunito", sans-serif;
  font-weight: 900;
  font-size: 58px;
  line-height: 1.04;
  letter-spacing: -0.025em;
  margin: 22px 0 0;
  color: $ink-strong;
  text-wrap: balance;
}
.hero-lead {
  font-size: 19px;
  line-height: 1.6;
  color: $muted;
  margin: 22px 0 0;
  max-width: 30em;
}
.hero-cta {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 32px;
}
.btn {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  font-family: "Nunito", sans-serif;
  font-weight: 800;
  font-size: 16px;
  border-radius: 14px;
  cursor: pointer;
  border: none;
  transition: background var(--ws-dur-fast) var(--ws-ease-out),
    transform var(--ws-dur-press) var(--ws-ease-out),
    box-shadow var(--ws-dur-fast) var(--ws-ease-out),
    border-color var(--ws-dur-fast) var(--ws-ease-out),
    color var(--ws-dur-fast) var(--ws-ease-out);
}
.btn-primary {
  color: #fff;
  padding: 15px 28px;
  background: $orange;
  box-shadow: 0 10px 24px rgba(234, 107, 38, 0.32);
  .heart {
    font-size: 17px;
  }
}
@media (hover: hover) and (pointer: fine) {
  .btn-primary:hover {
    background: $orange-dark;
    transform: translateY(-2px);
    box-shadow: 0 14px 30px rgba(234, 107, 38, 0.4);
  }
}
.btn-primary:active {
  transform: translateY(0) scale(0.97);
}
.btn-ghost {
  color: $ink;
  padding: 15px 26px;
  background: #fff;
  border: 1.5px solid rgba(42, 32, 24, 0.1);
}
.btn-ghost:hover {
  border-color: rgba(234, 107, 38, 0.45);
  color: $accent;
}
.btn-ghost:active {
  transform: scale(0.97);
}
.hero-stats {
  display: flex;
  align-items: center;
  gap: 26px;
  margin-top: 38px;
}
.stat-num {
  font-family: "Nunito", sans-serif;
  font-weight: 900;
  font-size: 28px;
  color: $orange;
  line-height: 1;
}
.stat-label {
  font-size: 13.5px;
  color: #8a7c6f;
  margin-top: 5px;
  font-weight: 600;
}
.stat-divider {
  width: 1px;
  height: 38px;
  background: rgba(42, 32, 24, 0.1);
}
.hero-media {
  position: relative;
}
.hero-photo {
  position: relative;
  border-radius: 28px;
  overflow: hidden;
  aspect-ratio: 4/5;
  box-shadow: 0 30px 60px -22px rgba(60, 36, 21, 0.35);
}
.hero-photo-shade {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(36, 27, 18, 0.32), transparent 45%);
  pointer-events: none;
}
/* ===== THREE CARDS ===== */
.cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}
.card {
  background: #fff;
  border-radius: 22px;
  padding: 34px 30px;
  border: 1px solid rgba(42, 32, 24, 0.05);
  box-shadow: 0 14px 40px -24px rgba(60, 36, 21, 0.28);
  display: flex;
  flex-direction: column;
  h3 {
    font-family: "Nunito", sans-serif;
    font-weight: 800;
    font-size: 22px;
    color: $ink-strong;
    margin-bottom: 11px;
  }
  p {
    font-size: 15.5px;
    line-height: 1.62;
    color: $muted;
    flex: 1;
  }
}
.card-icon {
  width: 52px;
  height: 52px;
  border-radius: 15px;
  background: #fbe3d2;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
  color: $accent;
}
.card-icon svg {
  width: 27px;
  height: 27px;
  display: block;
}
.card-link {
  margin-top: 20px;
  align-self: flex-start;
  font-family: "Nunito", sans-serif;
  font-weight: 800;
  font-size: 15px;
  color: $accent;
  cursor: pointer;
  text-decoration: none;
}
a.card-link:hover {
  text-decoration: underline;
}
.card-doneer {
  background: linear-gradient(160deg, #ea6b26, #d2541a);
  border: none;
  box-shadow: 0 18px 44px -20px rgba(234, 107, 38, 0.6);
  position: relative;
  overflow: hidden;
  h3 {
    color: #fff;
    position: relative;
  }
  p {
    color: rgba(255, 255, 255, 0.92);
    position: relative;
  }
}
.blob {
  position: absolute;
  top: -40px;
  right: -40px;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
}
.card-icon-light {
  background: rgba(255, 255, 255, 0.18);
  position: relative;
  color: #fff;
}
.card-cta-white {
  margin-top: 22px;
  align-self: flex-start;
  font-family: "Nunito", sans-serif;
  font-weight: 800;
  font-size: 15px;
  color: $accent;
  background: #fff;
  border: none;
  cursor: pointer;
  padding: 12px 22px;
  border-radius: 12px;
  position: relative;
  transition: transform var(--ws-dur-press) var(--ws-ease-out),
    box-shadow var(--ws-dur-fast) var(--ws-ease-out);
}
@media (hover: hover) and (pointer: fine) {
  .card-cta-white:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 22px -10px rgba(60, 36, 21, 0.4);
  }
}
.card-cta-white:active {
  transform: scale(0.97);
}

/* ===== TESTIMONIAL ===== */
.ws-quote {
  max-width: 920px;
  margin: 0 auto;
  padding: 56px 32px;
}
.quote-inner {
  text-align: center;
  padding: 0 24px;
}
.quote-mark {
  font-family: "Nunito", sans-serif;
  font-weight: 900;
  font-size: 90px;
  line-height: 0.6;
  color: #f2c9a8;
  margin-bottom: 8px;
}
.quote-text {
  font-family: "Nunito", sans-serif;
  font-weight: 600;
  font-style: italic;
  font-size: 30px;
  line-height: 1.42;
  color: $ink;
  letter-spacing: -0.01em;
  text-wrap: balance;
}
.quote-heart {
  color: #e0533a;
}
.quote-author {
  font-family: "Nunito", sans-serif;
  font-weight: 800;
  font-size: 16px;
  color: $accent;
  margin-top: 24px;
  letter-spacing: 0.02em;
}

/* ===== SPLIT SECTIONS ===== */
.split {
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 56px;
  align-items: center;
}
.split.reverse {
  grid-template-columns: 1.1fr 0.9fr;
}
.split-copy {
  h2 {
    font-family: "Nunito", sans-serif;
    font-weight: 900;
    font-size: 38px;
    line-height: 1.1;
    letter-spacing: -0.02em;
    color: $ink-strong;
    margin: 12px 0 20px;
  }
  p {
    font-size: 17px;
    line-height: 1.68;
    color: $muted;
    margin-bottom: 18px;
    &:last-child {
      margin-bottom: 0;
    }
  }
}
.split-media {
  border-radius: 26px;
  overflow: hidden;
  aspect-ratio: 1/1;
  box-shadow: 0 26px 54px -26px rgba(60, 36, 21, 0.32);
  &.tall {
    aspect-ratio: 4/5;
  }
}
.band-soft {
  background: #f6eadd;
  margin-top: 32px;
  .ws-wrap {
    padding: 72px 32px;
  }
}

/* ===== STAT BAND ===== */
.stat-band-wrap {
  padding: 16px 32px;
}
.stat-band {
  max-width: 1200px;
  margin: 0 auto;
  background: linear-gradient(150deg, #3b2415, #51301a);
  border-radius: 30px;
  padding: 64px 56px;
  position: relative;
  overflow: hidden;
}
.stat-blob {
  position: absolute;
  border-radius: 50%;
}
.stat-blob-1 {
  top: -60px;
  right: -30px;
  width: 220px;
  height: 220px;
  background: rgba(234, 107, 38, 0.16);
}
.stat-blob-2 {
  bottom: -80px;
  left: 40px;
  width: 180px;
  height: 180px;
  background: rgba(251, 176, 64, 0.1);
}
.stat-band-inner {
  position: relative;
  text-align: center;
  max-width: 760px;
  margin: 0 auto;
  p {
    font-size: 18px;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.78);
  }
}
.stat-headline {
  font-family: "Nunito", sans-serif;
  font-weight: 900;
  font-size: 46px;
  line-height: 1.12;
  letter-spacing: -0.02em;
  color: #fff;
  margin: 16px 0 18px;
  text-wrap: balance;
}
.amber {
  color: #fbb040;
}

/* ===== NIEUWS ===== */
.news-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 36px;
  h2 {
    font-family: "Nunito", sans-serif;
    font-weight: 900;
    font-size: 38px;
    line-height: 1.1;
    letter-spacing: -0.02em;
    color: $ink-strong;
    margin-top: 10px;
  }
}
.link-strong {
  font-family: "Nunito", sans-serif;
  font-weight: 800;
  font-size: 15px;
  color: $accent;
  white-space: nowrap;
  cursor: pointer;
  padding-bottom: 6px;
  text-decoration: none;
  transition: opacity var(--ws-dur-fast) var(--ws-ease-out);
}
.link-strong:hover {
  opacity: 0.7;
}
.news-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}
.news-card {
  background: #fff;
  border-radius: 22px;
  overflow: hidden;
  border: 1px solid rgba(42, 32, 24, 0.05);
  box-shadow: 0 14px 40px -26px rgba(60, 36, 21, 0.28);
  display: flex;
  flex-direction: column;
  cursor: pointer;
  color: inherit;
  text-decoration: none;
  transition: transform var(--ws-dur-fast) var(--ws-ease-out),
    box-shadow var(--ws-dur-fast) var(--ws-ease-out);
}
@media (hover: hover) and (pointer: fine) {
  .news-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 22px 50px -24px rgba(60, 36, 21, 0.36);
  }
}
.news-card:active {
  transform: translateY(-2px) scale(0.99);
}
.news-body {
  padding: 24px 24px 26px;
  display: flex;
  flex-direction: column;
  flex: 1;
  h3 {
    font-family: "Nunito", sans-serif;
    font-weight: 800;
    font-size: 19px;
    line-height: 1.28;
    color: $ink-strong;
    margin: 8px 0 12px;
  }
  p {
    font-size: 14.5px;
    line-height: 1.6;
    color: #6b5d52;
    flex: 1;
  }
}
.news-date {
  font-size: 13px;
  font-weight: 700;
  color: #a6907e;
}
.news-card .card-link {
  margin-top: 18px;
}

/* ===== RESPONSIVE ===== */
@media screen and (max-width: 900px) {
  .hero-grid {
    grid-template-columns: 1fr;
    gap: 36px;
  }
  .hero-title {
    font-size: 44px;
  }
  .cards {
    grid-template-columns: 1fr;
  }
  .split,
  .split.reverse {
    grid-template-columns: 1fr;
    gap: 32px;
  }
  .split.reverse .split-copy {
    order: 2;
  }
  .news-grid {
    grid-template-columns: 1fr;
  }
  .stat-headline {
    font-size: 34px;
  }
  .quote-text {
    font-size: 22px;
  }
  .news-head {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}

@media screen and (max-width: 600px) {
  .ws-wrap {
    padding: 36px 20px;
  }
  .hero {
    padding: 40px 20px 24px;
  }
  .hero-title {
    font-size: 36px;
  }
  .stat-band {
    padding: 44px 28px;
  }
  .band-soft .ws-wrap {
    padding: 48px 20px;
  }
}
</style>
